import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { Card, DatePicker, Select, Space, Switch, Typography } from 'antd';
import type { FlagSeverity, StudioLinkRelation, StudioListItem } from '../contract';
import {
  FlagStatus,
  STUDIO_PAGE_LIMIT,
  StudioViewer,
  getMonitorFlags,
  getMonitorLog,
  useStudioLoad,
} from '../api';
import {
  EMPTY_TEXT,
  FLAG_CODE_OPTIONS,
  FLAG_SCOPE_NOTE,
  FLAG_SEVERITY_OPTIONS,
  FLAG_STATUS_OPTIONS,
  LINK_RELATION_OPTIONS,
  LOG_ACTION_OPTIONS,
} from '../labels';
import FlagTable from '../components/flag-table';
import LogTable from '../components/log-table';
import { StudioErrorBlock, StudioFilterError, studioTitle, useClampPage } from './studio-list-tab';
import styles from '../styles.module.scss';

export interface MonitorTabProps {
  viewer: StudioViewer;
  /** Studio cho bộ lọc (kèm Quản lý, Trợ lý để gợi ý "Người thao tác"); có thể rỗng khi chưa tải xong. */
  studios: StudioListItem[];
  /** Lỗi tải danh sách Studio cho bộ lọc (hiện cạnh bộ lọc kèm "Thử lại"). */
  studiosError?: any;
  onReloadStudios?: () => void;
  /** Sau khi khép cờ (trang tải lại số trên nhãn tab). */
  onChanged?: () => void;
}

const DAY = 'YYYY-MM-DD';

const readId = (raw: string | null): number | undefined => {
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : undefined;
};

/**
 * Tab "Giám sát" (`/studios?tab=monitor`, mục 8.7.10; ADMIN và SMOD): khối "Cờ cần xem" (S29) và "Nhật ký
 * liên Studio" (S30). Bộ lọc chung: Studio (`?studioId=` từ ô đếm của danh sách), Người thao tác, khoảng ngày.
 * Chỉ Admin khép cờ (PATCH /approvals/flags/:id); SMOD chịu luật ẩn cờ sẵn có ở máy chủ.
 */
const MonitorTab = ({
  viewer,
  studios,
  studiosError,
  onReloadStudios,
  onChanged,
}: MonitorTabProps) => {
  const [params, setParams] = useSearchParams();
  const studioId = readId(params.get('studioId'));
  const [actorId, setActorId] = useState<number | undefined>(undefined);
  const [range, setRange] = useState<[string, string] | null>(null);
  // Khối cờ
  const [flagStatus, setFlagStatus] = useState<FlagStatus>('OPEN');
  const [severity, setSeverity] = useState<FlagSeverity | undefined>(undefined);
  const [code, setCode] = useState<string | undefined>(undefined);
  const [flagPage, setFlagPage] = useState(1);
  // Khối nhật ký
  const [actions, setActions] = useState<string[]>([]);
  const [relation, setRelation] = useState<StudioLinkRelation | undefined>(undefined);
  const [flagged, setFlagged] = useState(false);
  const [logPage, setLogPage] = useState(1);

  const from = range?.[0];
  const to = range?.[1];

  const flags = useStudioLoad(
    () =>
      getMonitorFlags({
        studioId,
        status: flagStatus,
        severity,
        code,
        actorId,
        from,
        to,
        page: flagPage,
        limit: STUDIO_PAGE_LIMIT,
      }),
    [studioId, flagStatus, severity, code, actorId, from, to, flagPage]
  );
  const log = useStudioLoad(
    () =>
      getMonitorLog({
        studioId,
        actorId,
        from,
        to,
        actions,
        relation,
        flagged,
        page: logPage,
        limit: STUDIO_PAGE_LIMIT,
      }),
    [studioId, actorId, from, to, actions, relation, flagged, logPage]
  );
  // Khép cờ cuối của trang cuối (đang lọc "Đang mở"): lùi trang thay vì kẹt ở trang rỗng.
  useClampPage(flagPage, setFlagPage, flags.data, flags.loading);
  useClampPage(logPage, setLogPage, log.data, log.loading);

  /** Đổi bộ lọc chung thì cả hai khối về trang 1. */
  const resetPages = () => {
    setFlagPage(1);
    setLogPage(1);
  };

  const setStudio = (value?: number) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('studioId', String(value));
        else next.delete('studioId');
        return next;
      },
      { replace: true }
    );
    resetPages();
  };

  const studioOptions = studios.map((s) => ({ value: s.id, label: studioTitle(s) }));
  if (studioId && !studioOptions.some((o) => o.value === studioId)) {
    studioOptions.unshift({ value: studioId, label: `Studio #${studioId}` });
  }

  // "Người thao tác": Quản lý, Trợ lý của các Studio + người thao tác có trong kết quả đang xem.
  const actorOptions = useMemo(() => {
    const names = new Map<number, string>();
    const add = (id: number | null | undefined, name?: string | null) => {
      if (id && name && !names.has(id)) names.set(id, name);
    };
    studios.forEach((s) => {
      if (s.manager) add(s.manager.id, `${s.manager.nickName} · Quản lý ${studioTitle(s)}`);
      s.assistants.forEach((a) => add(a.id, `${a.nickName} · Trợ lý ${studioTitle(s)}`));
    });
    (flags.data?.data || []).forEach((f) => add(f.actor?.id, f.actor?.name));
    (log.data?.data || []).forEach((r) => add(r.actor?.id, r.actor?.name));
    return Array.from(names.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'vi'));
  }, [studios, flags.data, log.data]);

  const rangeValue: [Dayjs, Dayjs] | null = range ? [dayjs(range[0]), dayjs(range[1])] : null;

  const afterFlagClosed = () => {
    flags.reload();
    log.reload();
    onChanged?.();
  };

  const flagsEmpty = flagStatus === 'OPEN' ? EMPTY_TEXT.monitorFlags : 'Không có cờ nào phù hợp.';

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Select<number>
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Tất cả Studio"
            value={studioId}
            options={studioOptions}
            style={{ width: 220 }}
            notFoundContent={studiosError ? 'Không tải được danh sách Studio.' : undefined}
            onChange={(value) => setStudio(value)}
            aria-label="Studio"
          />
          <Select<number>
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Người thao tác"
            value={actorId}
            options={actorOptions}
            style={{ width: 260 }}
            onChange={(value) => {
              setActorId(value);
              resetPages();
            }}
            aria-label="Người thao tác"
          />
          <DatePicker.RangePicker
            format="DD/MM/YYYY"
            value={rangeValue}
            onChange={(values) => {
              const [start, end] = values || [];
              setRange(start && end ? [start.format(DAY), end.format(DAY)] : null);
              resetPages();
            }}
            placeholder={['Từ ngày', 'Đến ngày']}
          />
        </div>
      </div>
      {studiosError && <StudioFilterError error={studiosError} onRetry={onReloadStudios} />}

      <Card size="small" title="Cờ cần xem">
        <div className={styles.page}>
          <div className={styles.filters}>
            <Select<FlagStatus>
              value={flagStatus}
              options={FLAG_STATUS_OPTIONS}
              style={{ width: 160 }}
              onChange={(value) => {
                setFlagStatus(value);
                setFlagPage(1);
              }}
              aria-label="Trạng thái cờ"
            />
            <Select<FlagSeverity>
              allowClear
              placeholder="Mọi mức"
              value={severity}
              options={FLAG_SEVERITY_OPTIONS}
              style={{ width: 190 }}
              onChange={(value) => {
                setSeverity(value);
                setFlagPage(1);
              }}
              aria-label="Mức cờ"
            />
            <Select<string>
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Mọi loại cờ"
              value={code}
              options={FLAG_CODE_OPTIONS}
              style={{ width: 220 }}
              onChange={(value) => {
                setCode(value);
                setFlagPage(1);
              }}
              aria-label="Loại cờ"
            />
          </div>
          {studioId && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {FLAG_SCOPE_NOTE}
            </Typography.Text>
          )}
          {flags.error && <StudioErrorBlock error={flags.error} onRetry={flags.reload} />}
          {!(flags.error && !flags.data) && (
            <FlagTable
              rows={flags.data?.data || []}
              loading={flags.loading}
              canClose={viewer.canAdmin}
              onClosed={afterFlagClosed}
              emptyText={flagsEmpty}
              pagination={{
                page: flagPage,
                pageSize: STUDIO_PAGE_LIMIT,
                total: flags.data?.metadata?.total ?? 0,
                onChange: setFlagPage,
              }}
            />
          )}
        </div>
      </Card>

      <Card size="small" title="Nhật ký liên Studio">
        <div className={styles.page}>
          <div className={styles.filters}>
            <Select<string[]>
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Mọi việc"
              value={actions}
              options={LOG_ACTION_OPTIONS}
              style={{ minWidth: 240, maxWidth: 480 }}
              maxTagCount="responsive"
              onChange={(value) => {
                setActions(value);
                setLogPage(1);
              }}
              aria-label="Việc"
            />
            <Select<StudioLinkRelation>
              allowClear
              placeholder="Mọi liên quan"
              value={relation}
              options={LINK_RELATION_OPTIONS}
              style={{ width: 180 }}
              onChange={(value) => {
                setRelation(value);
                setLogPage(1);
              }}
              aria-label="Liên quan"
            />
            <Space size={6}>
              <Switch
                checked={flagged}
                onChange={(value) => {
                  setFlagged(value);
                  setLogPage(1);
                }}
                aria-label="Chỉ dòng có cờ"
              />
              <span>Chỉ dòng có cờ</span>
            </Space>
          </div>
          {log.error && <StudioErrorBlock error={log.error} onRetry={log.reload} />}
          {!(log.error && !log.data) && (
            <LogTable
              rows={log.data?.data || []}
              loading={log.loading}
              showStudio
              showFlags
              canCloseFlag={viewer.canAdmin}
              onFlagClosed={afterFlagClosed}
              onDisputed={log.reload}
              emptyText={EMPTY_TEXT.monitorLog}
              pagination={{
                page: logPage,
                pageSize: STUDIO_PAGE_LIMIT,
                total: log.data?.metadata?.total ?? 0,
                onChange: setLogPage,
              }}
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default MonitorTab;
